import { useShallow } from 'zustand/react/shallow';

import { ReactComponent as ArrowPathExclamation } from '@/assets/icons/arrow-path-exclamation.svg';
import { Icon } from '@/components/core/Icon';
import { ValidationState } from '@/components/core/validationState/ValidationState';
import { useValidationsStore, ValidationStatus } from '@/components/datamodel/hooks/useValidationsStore';
import { useTranslation } from '@/hooks/useTranslation';

const hasAnyValidations = (validations: Record<string, unknown[]>) =>
    Object.values(validations).some((entries) => (entries?.length ?? 0) > 0);

const ItemComplianceIndicator = () => {
    const { t } = useTranslation();
    const { validationStatus, validationErrors, validationWarnings, localValidationErrors } =
        useValidationsStore(
            useShallow((state) => ({
                validationStatus: state.status,
                validationErrors: state.validationErrors,
                validationWarnings: state.validationWarnings,
                localValidationErrors: state.localValidationErrors,
            })),
        );

    if (validationStatus === ValidationStatus.IS_NOT_ASSIGNED) {
        return null;
    }

    if (validationStatus === ValidationStatus.IS_FAILED) {
        return (
            <Icon
                size="sm"
                variant="lighter-gray"
                icon={ArrowPathExclamation}
                title={t('VALIDATION_FAILED')}
                className="tw-h-5 tw-w-5 lg:tw-h-6 lg:tw-w-6"
            />
        );
    }

    // LAX-10105 removed the numeric badges, so we no longer need exact counts —
    // only whether the live validation store currently holds any error/warning.
    // Reading from the store (instead of the persisted item's static
    // `validation__` / `validation_warnings__` snapshots) keeps the icon in
    // sync as the user edits: it flips to green the moment the last error is
    // fixed, and to red the moment the first error appears.
    const hasErrors =
        hasAnyValidations(validationErrors) || hasAnyValidations(localValidationErrors);
    const hasWarnings = hasAnyValidations(validationWarnings);
    const isDirty =
        validationStatus === ValidationStatus.IS_RUNNING ||
        validationStatus === ValidationStatus.IS_DIRTY;
    const isCompliant = !hasErrors;

    return (
        <ValidationState
            layout="header"
            isDirty={isDirty}
            isCompliant={isCompliant}
            hasWarnings={hasWarnings}
        />
    );
};

export { ItemComplianceIndicator };
